import Draggable from '@components/DragAndDrop/components/Draggable/Draggable';
import { DraggableItemIds } from '@components/DragAndDrop/components/draggableItemIds';
import getDisplayName from '@lib/utils/getDisplayName';
import useService from '@modules/ServiceModule/useService';
import SoftPhoneModule from '@modules/SoftPhoneModule';
import { WebphoneCallState } from '@modules/SoftPhoneModule/types';
import TasksModule from '@modules/TasksModule';
import { useCallback, useEffect, useMemo } from 'react';
import { useWindowSize } from 'usehooks-ts';
import CallControls from './components/CallControls';
import { useCallDuration } from './hooks/useCallDuration';
import {
  setCallTimerStart,
  clearCallTimerStart,
} from './hooks/callTimerRegistry';
import useRecording from './hooks/useRecording';

const CALL_CONTROLS_WIDTH_PX = 777;
const CALL_CONTROLS_BOTTOM_OFFSET = 24;

interface CallBarProps {
  callState: WebphoneCallState;
  show: boolean;
}

export function CallBar({ callState, show }: CallBarProps) {
  const {
    callId,
    direction,
    established,
    hasSession,
    held,
    inTransfer,
    isAudioMuted,
    isVideoMuted,
    progress,
    remoteIdentity,
    terminating,
    remoteCallId,
  } = callState;
  const { userByContactCenterUserId } = useService();
  const isInCall = direction === 'terminating' ? established : hasSession;
  const task = TasksModule.getTaskByCallId(callId);

  const identity = useMemo(() => {
    const name = getDisplayName(
      task?.displayName ?? remoteIdentity,
      userByContactCenterUserId,
    );
    return name;
  }, [task?.displayName, remoteIdentity, userByContactCenterUserId]);

  const { stop, start, formattedTime } = useCallDuration();

  const handleStart = useCallback(() => {
    setCallTimerStart(callId);
    start();
  }, [callId, start]);

  const handleStop = useCallback(() => {
    clearCallTimerStart(callId);
    stop();
  }, [callId, stop]);

  const handleHangup = () => {
    handleStop();
    SoftPhoneModule.callControl.hangup(callId);
  };

  const handleAnswer = () => {
    handleStop();
    SoftPhoneModule.callControl.answer(callId);
  };

  const handleTransfer = useCallback(() => {
    SoftPhoneModule.callControl.hold();
  }, []);

  const {
    isRecording,
    isRecordingDisabled,
    isRecordingLoading,
    recordingTooltip,
    toggleRecording,
  } = useRecording(callId);

  // This useEffect stops the timer when the call ends
  useEffect(() => {
    if (!isInCall) {
      handleStop();
    }
  }, [isInCall, handleStop]);

  const { width: windowWidth } = useWindowSize();

  const handleCalculateAbsolutePosition = (x: number, y: number) => ({
    bottom: `${CALL_CONTROLS_BOTTOM_OFFSET - y}px`,
    left: windowWidth / 2 - CALL_CONTROLS_WIDTH_PX / 2 + x,
  });

  if (!callState) {
    return;
  }

  return isInCall ? (
    <Draggable
      id={`${DraggableItemIds.CALL_CONTROLS}-${callId}`}
      calculateAbsolutePosition={handleCalculateAbsolutePosition}
    >
      <CallControls
        show={show}
        established={established}
        formattedTime={formattedTime}
        hasSession={hasSession}
        held={held}
        inTransfer={inTransfer}
        isAudioMuted={isAudioMuted}
        isVideoMuted={isVideoMuted}
        phoneConnected={isInCall}
        onHangup={handleHangup}
        onAnswer={handleAnswer}
        onHold={() => SoftPhoneModule.callControl.hold()}
        onMute={() => SoftPhoneModule.callControl.mute()}
        onStartTimer={handleStart}
        onUnhold={() => SoftPhoneModule.callControl.unhold(callId)}
        onUnmute={() => SoftPhoneModule.callControl.unmute()}
        progress={progress}
        identity={identity}
        terminating={terminating}
        isRecording={isRecording}
        isRecordingDisabled={isRecordingDisabled}
        isRecordingLoading={isRecordingLoading}
        recordingTooltip={recordingTooltip}
        toggleRecording={() => toggleRecording(remoteCallId)}
        onTransfer={handleTransfer}
        task={task}
      />
    </Draggable>
  ) : null;
}
